import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { fetchTakers } from './services/appointmentService';
import type { AppointmentTaker } from '../marketplace/types';
import ConsultantCardGrid from './components/ConsultantCardGrid';
import { AppPanelShell } from '../../components/layout/AppPanelShell';
import { LAYOUT_MAX_WIDTH } from '../../components/layout/AppTopbar';
import { CWY_COLORS } from '../../theme/colors';

export const AppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointmentTakers, setAppointmentTakers] = useState<AppointmentTaker[]>([]);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchTakers()
      .then((all) => {
        const withSlug = all.filter((t) => t.slug && t.slug.trim() !== '');
        setAppointmentTakers(withSlug);
      })
      .catch(() => setError('Unable to load appointment options. Please try again later.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Box
      sx={{
        bgcolor: CWY_COLORS.pageBg,
        minHeight: '100%',
        py: { xs: 2, sm: 2.5 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: LAYOUT_MAX_WIDTH, mx: 'auto' }}>
        <AppPanelShell title="Appointments">
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Typography sx={{ fontSize: '0.9375rem', color: CWY_COLORS.textSecondary, mb: 3, maxWidth: 520 }}>
              Select a consultant, view their profile, and book a time that works for you. Sign in to complete
              payment.
            </Typography>

            {!loading && !error && appointmentTakers.length > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 3,
                  px: 1.75,
                  py: 0.75,
                  bgcolor: CWY_COLORS.pageBg,
                  border: `1px solid ${CWY_COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: CWY_COLORS.textSecondary,
                }}
              >
                <i className="ti ti-users" style={{ fontSize: 14, color: CWY_COLORS.red }} />
                {appointmentTakers.length} consultants · Walk-in or call available
              </Box>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                <CircularProgress sx={{ color: CWY_COLORS.red, mb: 1.5 }} />
                <Typography sx={{ color: CWY_COLORS.textSecondary, fontSize: '0.875rem' }}>
                  Loading consultants...
                </Typography>
              </Box>
            ) : error ? (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={load}>
                    Try again
                  </Button>
                }
              >
                {error}
              </Alert>
            ) : (
              <ConsultantCardGrid takers={appointmentTakers} />
            )}

            <Button
              onClick={() => navigate('/')}
              sx={{
                mt: 3,
                textTransform: 'none',
                color: CWY_COLORS.red,
                fontWeight: 600,
                px: 0,
              }}
            >
              ← Back to home
            </Button>
          </Box>
        </AppPanelShell>
      </Box>
    </Box>
  );
};
